import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, CircleAlert, TriangleAlert, Info } from 'lucide-react';
import { ApiError, ApiErrorHandler } from '../../utils/apiErrorHandler';
import { errorLoggingService } from '../../services/errorLoggingService';
import { cn } from '@/lib/utils';

interface ErrorNotificationProps {
  // Optional props for customization
  autoHideDuration?: number;
  maxErrors?: number;
}

interface ErrorDetails {
  error: ApiError;
  id: string;
  timestamp: Date;
}

type Severity = 'error' | 'warning' | 'info';

const SEVERITY_STYLES: Record<Severity, string> = {
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
};

function errorTitle(error: ApiError): string {
  switch (error.type) {
    case 'ValidationError': return 'Validation Error';
    case 'NotFoundError': return 'Not Found';
    case 'UnauthorizedError': return 'Unauthorized';
    case 'ForbiddenError': return 'Access Denied';
    case 'ExternalServiceError': return 'Service Error';
    case 'TimeoutError': return 'Request Timeout';
    case 'InternalServerError': return 'Server Error';
    case undefined:
    default: return 'Error';
  }
}

function getSeverity(error: ApiError): Severity {
  if (error.status >= 500 || error.status === 0) return 'error';
  if (error.status >= 400) return 'warning';
  return 'info';
}

function getErrorIcon(error: ApiError) {
  if (error.status >= 500) return <CircleAlert className="h-5 w-5" />;
  if (error.status >= 400) return <TriangleAlert className="h-5 w-5" />;
  return <Info className="h-5 w-5" />;
}

/**
 * A single stacked error toast. Owns its own auto-hide timer so server errors
 * (>= 500) stick until dismissed while everything else fades after
 * autoHideDuration — parity with the old MUI Snackbar.
 */
function ErrorToast({
  errorDetail,
  index,
  autoHideDuration,
  expanded,
  onToggle,
  onClose,
}: {
  errorDetail: ErrorDetails;
  index: number;
  autoHideDuration: number;
  expanded: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const { error } = errorDetail;
  const severity = getSeverity(error);

  useEffect(() => {
    if (error.status >= 500) return; // sticky
    const timer = setTimeout(onClose, autoHideDuration);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div
      className={cn(
        'fixed right-6 z-50 flex min-w-[300px] max-w-[500px] gap-2 rounded-md border p-3 shadow-lg',
        SEVERITY_STYLES[severity]
      )}
      style={{ top: `${24 + index * 80}px` }}
      role="alert"
    >
      <span className="mt-0.5 shrink-0">{getErrorIcon(error)}</span>
      <div className="flex-1">
        <p className="font-semibold">{errorTitle(error)}</p>
        <p className="text-sm">{error.message}</p>

        {expanded && (
          <div className="mt-1">
            {error.errors && (
              <div className="mb-1">
                <p className="text-xs opacity-80">Validation Errors:</p>
                {Object.entries(error.errors).map(([field, messages]) => (
                  <div key={field} className="ml-1">
                    <p className="text-xs">
                      <strong>{field}:</strong> {(messages as string[]).join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {error.correlationId && (
              <p className="text-xs opacity-80">Error ID: {error.correlationId}</p>
            )}

            {error.status === 0 && (
              <div className="mt-1">
                <button
                  onClick={handleRetry}
                  className="text-xs font-medium underline underline-offset-2"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-start">
        {error.shouldShowDetails() && error.errors && (
          <button
            onClick={onToggle}
            className="rounded p-0.5 hover:bg-black/5"
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
        <button
          onClick={onClose}
          className="rounded p-0.5 hover:bg-black/5"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  autoHideDuration = 6000,
  maxErrors = 3
}) => {
  const [errors, setErrors] = useState<ErrorDetails[]>([]);
  const [expandedError, setExpandedError] = useState<string | null>(null);

  useEffect(() => {
    // Register error callback
    const handleError = (error: ApiError) => {
      const errorDetail: ErrorDetails = {
        error,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date()
      };

      setErrors(prev => {
        // Keep only the latest maxErrors
        const newErrors = [errorDetail, ...prev].slice(0, maxErrors);
        return newErrors;
      });

      // Log the error
      errorLoggingService.logError(
        error.message,
        error,
        error.type || 'ApiError',
        {
          status: error.status,
          correlationId: error.correlationId
        }
      );
    };

    ApiErrorHandler.registerErrorCallback(handleError);

    return () => {
      ApiErrorHandler.unregisterErrorCallback(handleError);
    };
  }, [maxErrors]);

  const handleClose = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
    if (expandedError === id) {
      setExpandedError(null);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpandedError(expandedError === id ? null : id);
  };

  if (errors.length === 0) {
    return null;
  }

  return (
    <>
      {errors.map((errorDetail, index) => (
        <ErrorToast
          key={errorDetail.id}
          errorDetail={errorDetail}
          index={index}
          autoHideDuration={autoHideDuration}
          expanded={expandedError === errorDetail.id}
          onToggle={() => toggleExpanded(errorDetail.id)}
          onClose={() => handleClose(errorDetail.id)}
        />
      ))}
    </>
  );
};

export default ErrorNotification;
